import './patientAddScreen.scss'
import {ITimelineItem} from "../../../shared/models/timeline.model";
import Grid from "@mui/material/Grid";
import React, {useCallback, useEffect, useState} from "react";
import _ from "lodash";
import {useNavigate, useSearchParams} from "react-router-dom";
import {CommonService} from "../../../shared/services";
import {IAPIResponseType} from "../../../shared/models/api.model";
import PatientInformationComponent from "./patient-information/patientInformationComponent";
import ContactDetailsComponent from "./patient-contact-details/contactDetailsComponent";
import PatientInsuranceComponent from "./patient-insurance/patientInsuranceComponent";
import {ImageConfig} from "../../../constants";
import TimelineComponent from "../../../shared/components/timeline/timelineComponent";
import {useDispatch, useSelector} from "react-redux";
import {setCurrentNavParams, setSideMenuView} from "../../../store/actions/header.action";
import LottieFileGenarationComponent
    from "../../../shared/components/core/lottie-file-genaration/LottieFileGenarationComponent";
import {IRootReducerState} from "../../../store/reducers";
import AdditionalDetailsComponent from './additional-details/AdditionalDetailsComponent';

interface PatientAddScreenProps {

}

interface ICreateTestOrderPayload {
    facilityId: number;
    locationId: number;
    patientId: number;
    // isPatientConsentSigned: boolean;
}

const PATIENT_INFORMATION = 'patient_information';
const CONTACT_DETAILS = 'contact_details';
const INSURANCE = 'insurance';
const ADDITIONAL_DETAILS = 'additional_details';

const STEPS: ITimelineItem[] = [
    {
        id: PATIENT_INFORMATION,
        title: "Patient Information",
    },
    {
        id: ADDITIONAL_DETAILS,
        title: "Additional Details",
    },
    {
        id: CONTACT_DETAILS,
        title: "Contact Details",
    },
    {
        id: INSURANCE,
        title: "Insurance",
    },
];

const PatientAddScreen = (props: PatientAddScreenProps) => {

        const {queryParamData} = useSelector((state: IRootReducerState) => state.stateManagement);
        const [steps] = useState<ITimelineItem[]>([..._.cloneDeep(STEPS)]);
        const [searchParams, setSearchParams] = useSearchParams();
        const [currentStep, setCurrentStep] = useState<ITimelineItem | undefined>(STEPS[0]);
        const [patientID, setPatientID] = useState<number | undefined>(undefined);
        const [activeStep, setActiveStep] = React.useState(0);
        const [patientDetails, setPatientDetails] = useState<any>(undefined);
        // const [isConsentCollectionModalOpen, setIsConsentCollectionModalOpen] = useState<boolean>(false);
        const navigate = useNavigate();
        const dispatch = useDispatch();

        useEffect(() => {
            dispatch(setCurrentNavParams("", "Patient"));
            dispatch(setSideMenuView('compact'));
        }, [dispatch]);

        const [isPatientDetailsLoading, setIsPatientDetailsLoading] = useState<boolean>(false);

        const updateSteps = useCallback((currentStepIndex: number) => {
            setCurrentStep(STEPS[currentStepIndex]);
            searchParams.set("step", STEPS[currentStepIndex].id);
            setSearchParams(searchParams);
            setActiveStep(currentStepIndex);
        }, [searchParams, setSearchParams])

        const getPatientDetails = useCallback(() => {
            const payload = {};
            let patientDetails: any = {};
            setIsPatientDetailsLoading(true);
            CommonService._patient.getPatientDetails(patientID, payload)
                .then((response: IAPIResponseType<any>) => {
                    if (response?.data) {
                        patientDetails = response.data;
                    }
                    setPatientDetails(patientDetails);
                    setIsPatientDetailsLoading(false);
                })
                .catch((error: any) => {
                    setPatientDetails(patientDetails);
                    setIsPatientDetailsLoading(false);
                });
        }, [patientID]);


        const handlePrevious = useCallback((currentStepIndex: number, currentStepId: string) => {
            if ([INSURANCE].includes(currentStepId)) {
                updateSteps(currentStepIndex);
            } else {
                // do nothing;
            }
        }, [updateSteps]);

        const handlePatientCancel = useCallback(() => {
            CommonService.openConfirmationDialog({
                confirmationTitle: 'Please confirm!',
                confirmationSubTitle: 'Are you sure you want to exit without adding this patient?',
                disableEscKeyClose: false,
                disableBackdropClose: true,
                imagePath: `${ImageConfig.ConfirmationImage}`,
                direction: "up",
                yes: {
                    text: "Exit",
                    color: "primary"
                },
                no: {
                    text: "Cancel",
                    color: "primary"
                }
            }).then(() => {
                navigate(CommonService._routeConfig.PatientListRoute());
            })
                .catch(() => {
                });
        }, [navigate]);

        const createTestOrderByPatientAndRedirect = useCallback((payload: ICreateTestOrderPayload) => {
            CommonService._testOrder.createNewTestOrder(payload)
                .then((response: IAPIResponseType<any>) => {
                    const testOrderId = response?.data?.id//test order id
                    /*
                    after new test order creating directly redirecting to the add test order 2nd step
                     */
                    navigate(`${queryParamData?.returnUrl}` + CommonService.setUrlSearchParams({
                        testOrderId: testOrderId,
                        step: "test_order_details"
                    }))

                })
                .catch((error: any) => {
                    CommonService._alert.showToast(error?.error || "Failed to create new test order", "error");
                });
        }, [navigate, queryParamData?.returnUrl])

        // flow changed before callback expecting isPatientConsentSigned?: boolean, now we are not showing the consent popup modal to collect consent
        const handlePatientConsentCollection = useCallback(() => {
            const payload: ICreateTestOrderPayload = {
                facilityId: Number(queryParamData?.facilityId),
                locationId: Number(queryParamData?.locationId),
                patientId: Number(queryParamData?.patientId),
                // isPatientConsentSigned: isPatientConsentSigned
            }
            createTestOrderByPatientAndRedirect(payload);
        }, [createTestOrderByPatientAndRedirect, queryParamData?.facilityId, queryParamData?.locationId, queryParamData.patientId]);


        const handleADDSuccess = useCallback(() => {
            CommonService.openConfirmationDialog({
                confirmationTitle: 'Congratulations! ',
                confirmationSubTitle: 'Patient has been successfully created.',
                disableEscKeyClose: false,
                disableBackdropClose: true,
                imagePath: `${ImageConfig.CongratulationImage}`,
                direction: "up",
                hideNoOption: true,
                yes: {
                    text: "Proceed",
                    color: "primary"
                }

            }).then(() => {
                if (queryParamData?.returnUrl) {
                    // setIsConsentCollectionModalOpen(true);
                    handlePatientConsentCollection();
                } else {
                    navigate(CommonService._routeConfig.PatientListRoute());
                }
            })
                .catch(() => {
                });
        }, [navigate, queryParamData?.returnUrl, handlePatientConsentCollection]);


        const handleNext = useCallback((currentStepIndex: number, currentStepId: string, patientId?: number, data?: any) => {
            switch (currentStepId) {
                case PATIENT_INFORMATION:
                    if (patientId) {
                        setPatientID(patientId);
                        searchParams.set("patientId", patientId.toString());
                        setSearchParams(searchParams);
                    }
                    updateSteps(currentStepIndex);
                    break;

                case ADDITIONAL_DETAILS:
                    if (data) {
                        setPatientDetails((oldSate: any) => {
                            return {
                                ...oldSate, ...data
                            }
                        })
                    }
                    updateSteps(currentStepIndex);
                    break;

                case CONTACT_DETAILS:
                    if (data) {
                        setPatientDetails((oldSate: any) => {
                            return {
                                ...oldSate, ...data
                            }
                        })
                    }
                    updateSteps(currentStepIndex);
                    break;

                case INSURANCE:
                    handleADDSuccess();
                    break;
                default:
                    <></>
            }
        }, [searchParams, setSearchParams, handleADDSuccess, updateSteps]);

        useEffect(() => {
            const step = searchParams.get("step");
            setActiveStep(STEPS.findIndex((stepItem: any) => stepItem.id === step));
            const patientId = searchParams.get("patientId");
            const stepIDS = STEPS.map(item => item.id);
            let stepIndex = 0;
            if (patientId) {
                setPatientID(parseInt(patientId, 10));
                if (stepIDS.indexOf(step) > -1) {
                    if (step !== PATIENT_INFORMATION) {
                        stepIndex = stepIDS.indexOf(step);
                    } else {
                        stepIndex = 1;
                    }
                } else {
                    stepIndex = 1;
                }
            } else {
                stepIndex = 0;
                setActiveStep(0)
            }
            setCurrentStep(STEPS[stepIndex]);
        }, [searchParams]);

        const renderComponent = useCallback(() => {
            if (!patientID) {
                if (currentStep?.id === PATIENT_INFORMATION) {
                    return <PatientInformationComponent
                        next={(patientId: number) => handleNext(1, PATIENT_INFORMATION, patientId)}
                        cancel={handlePatientCancel}
                    />
                }
            } else {
                switch (currentStep && currentStep.id) {
                    case ADDITIONAL_DETAILS:
                        return <AdditionalDetailsComponent
                            patientID={patientID}
                            patientDetails={patientDetails}
                            cancel={handlePatientCancel}
                            next={(data) => handleNext(2, ADDITIONAL_DETAILS, undefined, data)}
                        />
                    case CONTACT_DETAILS:
                        return <ContactDetailsComponent
                            patientID={patientID}
                            patientDetails={patientDetails}
                            cancel={handlePatientCancel}
                            next={(data: number) => handleNext(3, CONTACT_DETAILS, undefined, data)}
                        />
                    case INSURANCE:
                        return <PatientInsuranceComponent
                            patientID={patientID}
                            patientDetails={patientDetails}
                            cancel={handlePatientCancel}
                            next={(data: number) => handleNext(4, INSURANCE, undefined, data)}
                            prev={() => handlePrevious(2, INSURANCE)}
                        />
                    default:
                        <></>
                }
            }
        }, [patientID, patientDetails, currentStep, handleNext, handlePrevious, handlePatientCancel]);

        useEffect(() => {
            if (patientID) {
                getPatientDetails();
            }
        }, [patientID, getPatientDetails]);

        // @ts-ignore
        return (
            <>
                <div className="patient-creation-layout-screen add-layout-with-timeline screen ">
                    <Grid container spacing={2} className="add-form-timeline-wrapper">
                        <Grid item sm={12} md={3} xl={3} className="timeline-layout"
                              sx={{
                                  display: {xs: "none", md: "block"}
                              }}>
                            <div className="add-layout-title">Adding Patient</div>
                            <div className="add-layout-timeline">
                                <TimelineComponent
                                    activeStep={activeStep}
                                    steps={steps}
                                />
                            </div>
                        </Grid>
                        <Grid item xs={12} sm={12} md={9} xl={9} className="add-form-content">
                            <div className="add-layout-form-content">
                                {renderComponent()}
                            </div>
                        </Grid>
                    </Grid>
                    {isPatientDetailsLoading &&
                        <div className={"glass-effect h-v-center flex-direction-column"}>
                            <LottieFileGenarationComponent
                                loop={true}
                                autoplay={true}
                                animationData={ImageConfig.LoadingLottieFile}/>
                        </div>
                    }
                </div>
                {/* <ConsentCollectionComponent
                    isOpen={isConsentCollectionModalOpen}
                    onClose={() => setIsConsentCollectionModalOpen(false)}
                    onConfirm={handlePatientConsentCollection}
                    patientName={`${patientDetails?.firstName || ''} ${patientDetails?.lastName || ''}`}
                /> */}
            </>
        )

    }

;

export default PatientAddScreen;
